import { AuthorizedRoute } from '@/components/AuthorizedRoute/AuthorizedRoute'
import { DataTag } from '@/components/DataTag/DataTag'
import { ApiariesProvider, useApiaries } from '@/contexts/useApiaries'
import type { DeploymentLabelOption } from '@/contexts/useApiaries'
import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import {
  useGetAllClustersRevisions,
  useGetTags,
} from '@/generated/http-clients/v3ApiComponents'
import type { LatestClusterRevision } from '@/generated/http-clients/v3ApiSchemas'
import { timeAgo } from '@/helpers/timeFormatter'
import { clustersRootRoute } from '@/router/rootRoutes'
import {
  Button,
  Card,
  Checkbox,
  CloseButton,
  Collapsible,
  Dialog,
  Field,
  Flex,
  HStack,
  Icon,
  List,
  Select,
  Table,
  Tag,
  Text,
  VStack,
  createListCollection,
  useDisclosure,
  useSelectContext,
} from '@chakra-ui/react'
import { Link, createRoute, useNavigate } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, Plus } from 'lucide-react'
import { type UIEvent, useCallback, useEffect, useMemo, useState } from 'react'

import { useClusterConfigFilters } from '../../hooks'

import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import type { FilterOption } from '@/components/FilterMenu'
import type { ValueLabelOption } from '@/components/Renderers/types'
import SkeletonTable from '@/components/SkeletonTable'
import TableFooter from '@/components/TableFooter/TableFooter'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { useClusterUpdates } from '@/contexts/useClusterUpdates'
import { useTemplates } from '@/contexts/useTemplates'
import { ClusterConfigFilters } from '@/features/ClusterConfigs/components/ClusterConfigFilters'
import { ClusterConfigCombinedStates } from '@/features/ClusterConfigs/components/States/ClusterConfigCombinedStates'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import { FIRST_PAGE } from '@/helpers/constants'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  toaster,
} from '@/hooks/useToast/toastUtils'
import { NBSP, capitalize, decodeTagValue } from '@/utils'
import type { UseQueryResult } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'
import { ClusterSyncDisabled } from '../../components/States/ClusterSyncDisabled'
import revisionStates from '../../utils/revisionStates'
import { clusterConfigsRootRoute } from '../ClusterConfigsPage/ClusterConfigsPage'
import styles from './clusterConfigsTable.module.css'

// Define search params type to avoid type issues
type ClusterConfigSearchParams = {
  page: number
  order?: string
  apiaryId?: string
  orgId?: string
  includeDisabled?: boolean
  templateId?: string | number
  approvalState?: string
  hiveState?: string
  tags?: string
  hiveId?: string
  compact?: boolean
}

export const clusterConfigTableRoute = createRoute({
  getParentRoute: () => clustersRootRoute,
  path: '/',
  validateSearch: (
    search: Record<string, unknown>,
  ): ClusterConfigSearchParams => {
    const isStringOrNumber = ['string', 'number'].includes(
      typeof search.templateId,
    )
    let templateId: string | number | undefined = isStringOrNumber
      ? (search.templateId as string | number)
      : undefined
    if (typeof templateId === 'string' && templateId.startsWith('"')) {
      templateId = templateId.replace(/^"(.*)"$/, '$1')
    }

    return {
      page: search.page ? Number(search.page) : 1,
      order: typeof search.order === 'string' ? search.order : undefined,
      apiaryId:
        typeof search.apiaryId === 'string' ? search.apiaryId : undefined,
      orgId: typeof search.orgId === 'string' ? search.orgId : undefined,
      // Handle includeDisabled as a boolean properly - checking for 'true' or true
      includeDisabled:
        search.includeDisabled === 'true' || search.includeDisabled === true,
      templateId,
      approvalState:
        typeof search.approvalState === 'string'
          ? search.approvalState
          : undefined,
      hiveState:
        typeof search.hiveState === 'string' ? search.hiveState : undefined,
      tags: typeof search.tags === 'string' ? search.tags : undefined,
      hiveId: typeof search.hiveId === 'string' ? search.hiveId : undefined,
      compact: search.compact === 'true' || search.compact === true,
    }
  },
  component: () => (
    <AuthorizedRoute>
      <ApiariesProvider>
        <ClusterConfigTable />
      </ApiariesProvider>
    </AuthorizedRoute>
  ),
})

enum SortOrder {
  Ascending = 'asc',
  Descending = 'desc',
}

const SelectValueWithTag = ({ placeholder }: { placeholder: string }) => {
  const select = useSelectContext()
  const items = select.selectedItems as { value: string; label: string }[]

  return (
    <Select.ValueText placeholder={placeholder}>
      {items.length > 0 ? (
        <HStack gap={2}>
          <Tag.Root size="sm">
            <Tag.Label>{items[0].value}</Tag.Label>
          </Tag.Root>
          <Text>{items[0].label}</Text>
        </HStack>
      ) : null}
    </Select.ValueText>
  )
}

export function ClusterConfigTable() {
  const { hasAdminRights } = useRBACPermissions()
  const navigate = useNavigate()
  const { t } = useTranslation(undefined, { useSuspense: false })
  const { deploymentLabels } = useApiaries()

  const searchParams = clusterConfigTableRoute.useSearch()

  const { open: isOpen, onOpen, onClose } = useDisclosure()

  const { templatesData, templatesState, loadMoreTemplates } = useTemplates()

  const { submitBulkClusterUpdate } = useClusterUpdates()

  // Check if user has admin rights before making API calls
  const canAccessClusterRevisions = hasAdminRights()

  // Create sort options with translations
  const sortOrderOptions = useMemo(
    () => [
      {
        value: SortOrder.Descending,
        label: t('clusterConfigTable.sortOptions.newestFirst'),
      },
      {
        value: SortOrder.Ascending,
        label: t('clusterConfigTable.sortOptions.oldestFirst'),
      },
    ],
    [t],
  )

  // Define approval state options with translations
  const approvalStateOptions = useMemo(
    (): FilterOption[] => [
      { value: 'submitted', label: t('Submitted') },
      { value: 'approved', label: t('Approved') },
      { value: 'applied', label: t('Applied') },
      { value: 'rejected', label: t('Rejected') },
    ],
    [t],
  )

  // Define hive state options with translations
  const hiveStateOptions = useMemo(
    (): FilterOption[] => [
      { value: 'RUNNING', label: t('Running') },
      { value: 'PENDING', label: t('Pending') },
      { value: 'ERROR', label: t('Error') },
      { value: 'DELETING', label: t('Deleting') },
      { value: 'LOCKED', label: t('Locked') },
      { value: 'UNKNOWN', label: t('Unknown') },
    ],
    [t],
  )

  // Get page from URL or default to 1
  const [page, setPage] = useState(searchParams.page || 1)

  // Filter state for dropdowns - initialize from URL params if available
  const [selectedDeploymentLabel, setSelectedDeploymentLabel] =
    useState<DeploymentLabelOption | null>(null)

  // Update selected deployment label when deploymentLabels are loaded and there's an apiaryId in URL
  useEffect(() => {
    if (searchParams.apiaryId && deploymentLabels.length > 0) {
      const foundLabel = deploymentLabels.find(
        (label) => label.value === searchParams.apiaryId,
      )
      if (foundLabel) {
        setSelectedDeploymentLabel(foundLabel)
      }
    }
  }, [searchParams.apiaryId, deploymentLabels])

  const [selectedSortOption, setSelectedSortOption] = useState<FilterOption>(
    () => {
      const order = searchParams.order || SortOrder.Descending
      return {
        value: order,
        label:
          order === SortOrder.Descending
            ? t('clusterConfigTable.sortOptions.newestFirst')
            : t('clusterConfigTable.sortOptions.oldestFirst'),
      }
    },
  )

  // Initialize selectedTemplate directly from URL
  const [selectedTemplate, setSelectedTemplate] = useState<FilterOption | null>(
    () => {
      if (searchParams.templateId) {
        // The actual label will be updated when templatesData is loaded
        // Ensure the ID is a clean string without quotes
        const templateId = String(searchParams.templateId).replace(
          /^"(.*)"$/,
          '$1',
        )

        // Find the actual template in templatesData if available
        const template = templatesData?.templates?.find(
          (t) => t.template_id === templateId,
        )

        return {
          value: templateId,
          label: template?.name || templateId,
        }
      }
      return null
    },
  )

  // Update template name when templates data becomes available
  useEffect(() => {
    if (
      searchParams.templateId &&
      templatesData?.templates &&
      selectedTemplate
    ) {
      const cleanTemplateId = String(searchParams.templateId).replace(
        /^"(.*)"$/,
        '$1',
      )

      // Find the matching template to get its name
      const matchedTemplate = templatesData.templates.find(
        (t) => t.template_id === cleanTemplateId,
      )

      if (matchedTemplate && matchedTemplate.name !== selectedTemplate.label) {
        setSelectedTemplate({
          value: cleanTemplateId,
          label: matchedTemplate.name || cleanTemplateId,
        })
      }
    }
  }, [searchParams.templateId, templatesData?.templates, selectedTemplate])

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(
    searchParams.orgId || null,
  )

  // State variables for approval state and hive state
  const [selectedApprovalState, setSelectedApprovalState] =
    useState<FilterOption | null>(() => {
      if (searchParams.approvalState) {
        return {
          value: searchParams.approvalState,
          label: capitalize(searchParams.approvalState),
        }
      }
      return null
    })

  const [selectedHiveState, setSelectedHiveState] =
    useState<FilterOption | null>(() => {
      if (searchParams.hiveState) {
        return {
          value: searchParams.hiveState,
          label: capitalize(searchParams.hiveState),
        }
      }
      return null
    })

  // State variable for tag filters
  const [selectedTags, setSelectedTags] = useState<FilterOption[]>(() => {
    if (searchParams.tags) {
      const tagValues = searchParams.tags.split(',')
      return tagValues.map((val) => {
        // Decode the value properly to handle spaces and + characters
        const decodedValue = decodeTagValue(val)
        return { value: decodedValue, label: decodedValue }
      })
    }
    return []
  })

  // State variable for hive ID filter
  const [selectedHiveId, setSelectedHiveId] = useState<string | null>(
    searchParams.hiveId || null,
  )

  const [bulkUpdateTemplateId, setBulkUpdateTemplateId] =
    useState<ValueLabelOption | null>(null)

  const [includeDisabled, setIncludeDisabled] = useState(
    searchParams.includeDisabled || false,
  )

  const compact = searchParams.compact || false

  const [selectedHives, setSelectedHives] = useState<string[]>([])

  // Fetch available tags
  const tagsQuery = useGetTags(
    {},
    {
      staleTime: 5 * 60 * 1000, // 5 minutes - tags don't change often
    },
  )

  // Transform tags into filter options
  const tagOptions = useMemo(() => {
    return (tagsQuery.data?.tags || []).map((tag) => ({
      label: tag,
      value: tag,
    }))
  }, [tagsQuery.data?.tags])

  // Create templates collection for Select component
  const templatesCollection = useMemo(
    () =>
      createListCollection({
        items: (templatesState.data || []).map((data) => ({
          value: data.template_id,
          label: data.name ?? data.template_id,
        })),
      }),
    [templatesState.data],
  )

  // Scroll handler for templates infinite scroll
  const handleTemplatesScroll = (e: UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    const bottom =
      Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 1
    if (bottom) {
      loadMoreTemplates()
    }
  }

  // Get enhanced query parameters and active filters state from custom hook
  const { enhancedQueryParams, hasActiveFilters } = useClusterConfigFilters({
    page,
    selectedSortOption,
    selectedDeploymentLabel,
    selectedTemplate,
    includeDisabled,
    selectedOrgId,
    selectedApprovalState,
    selectedHiveState,
    selectedTags,
    selectedHiveId,
    compact,
  })

  // Optimize cluster data fetching
  const clusters = useGetAllClustersRevisions(
    {
      queryParams: enhancedQueryParams,
    },
    {
      // Skip the query if user doesn't have admin rights
      enabled: canAccessClusterRevisions,
      // Only refetch on a timer, not on mount or focus
      refetchInterval: 20000, // 20s
      refetchOnMount: false,
      refetchOnWindowFocus: false,

      // Improve cache behavior to prevent unnecessary refetches
      staleTime: 10000, // 10s - data is considered fresh for 10s
      gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for 5 minutes

      // Keep previous data while loading new data to prevent layout shifts
      placeholderData: (previousData) => previousData,
    },
  )

  function buildPaginationUrl(
    newPage: number,
    queryParams: Record<string, unknown>,
  ): string {
    const searchParams = new URLSearchParams()
    searchParams.append('page', String(newPage))

    for (const [key, value] of Object.entries(queryParams)) {
      if (key !== 'page' && value !== undefined) {
        searchParams.append(key, String(value))
      }
    }

    return `${window.location.pathname}?${searchParams.toString()}`
  }

  // detect when the list of cluster revisions has been updated
  useEffect(() => {
    // when the cluster revisions data is updated, check whether items
    // in selectedHives still exist in the cluster revision list.
    // Remove all the ones that do not exist anymore.
    const validIds =
      clusters?.data?.revisions?.map(({ revision }) => revision.hive_id) || []
    setSelectedHives((prevSelected) =>
      prevSelected.filter((id) => validIds.includes(id)),
    )
  }, [clusters?.data?.revisions])

  function handleNextPage() {
    if (clusters.data?.has_next_page) {
      const newPage = page + 1
      setPage(newPage)

      const urlString = buildPaginationUrl(newPage, enhancedQueryParams)
      navigate({
        to: urlString,
        replace: true,
      })
    }
  }

  function handlePreviousPage() {
    if (page > 1) {
      const newPage = page - 1
      setPage(newPage)

      const urlString = buildPaginationUrl(newPage, enhancedQueryParams)
      navigate({
        to: urlString,
        replace: true,
      })
    }
  }

  function goToFirstPage() {
    setPage(FIRST_PAGE)

    const urlString = buildPaginationUrl(FIRST_PAGE, enhancedQueryParams)
    navigate({
      to: urlString,
      replace: true,
    })
  }

  function goToLastPage() {
    const newPage = clusters.data?.total_pages ?? page
    setPage(newPage)

    const urlString = buildPaginationUrl(newPage, enhancedQueryParams)
    navigate({
      to: urlString,
      replace: true,
    })
  }

  function revisionActionMetadata(latestRevision: LatestClusterRevision) {
    const { revision } = latestRevision
    let time = revision.rollout?.updated_at
      ? timeAgo(revision.rollout?.updated_at ?? '')
      : undefined
    let author = revisionStates.isSubmitted(latestRevision)
      ? revision.rollout?.created_by
      : revision.rollout?.approved_by
    if (!author) {
      author = revision.rollout?.created_by
    }
    // only in the submitted case: if updated_at is undefined but created_at is, use that field
    if (
      revisionStates.isSubmitted(latestRevision) &&
      !time &&
      revision.rollout?.created_at
    ) {
      time = timeAgo(revision.rollout?.created_at)
    }

    const prefix = time ? `${time} ` : ''
    return `${prefix}by ${author}`
  }

  // Function to generate a URL with current filter parameters without navigating
  const generateFilterUrl = useCallback(() => {
    const searchParams = new URLSearchParams()

    // Add each param to the search params
    for (const [key, value] of Object.entries(enhancedQueryParams)) {
      if (value !== undefined) {
        searchParams.append(key, String(value))
      }
    }

    const baseUrl = window.location.href.split('?')[0]
    return `${baseUrl}?${searchParams.toString()}`
  }, [enhancedQueryParams])

  // Import toast from Chakra UI at the top of the file
  // Function to copy the current filters URL to clipboard
  const copyFilterUrl = () => {
    const url = generateFilterUrl()
    navigator.clipboard
      .writeText(url)
      .then(() => {
        // Show success toast
        toaster.create({
          ...SUCCESS_TOAST_OPTION,
          title: t('clusterConfigTable.share.copySuccess'),
        })
      })
      .catch(() => {
        // Show error toast
        toaster.create({
          ...ERROR_TOAST_OPTION,
          title: t('clusterConfigTable.share.copyError'),
        })
      })
  }

  // Memoized filter handlers to prevent unnecessary recreations
  // Filter handlers
  // Helper function to update URL without cluttering history
  const updateUrlWithoutHistoryEntry = useCallback(
    (params: Partial<ClusterConfigSearchParams>) => {
      // Use URLSearchParams to create the query string (just like in generateFilterUrl)
      const searchParams = new URLSearchParams()

      // Always include page
      searchParams.append('page', String(params.page || 1))

      // Only include properties with values
      if (params.order) {
        searchParams.append('order', String(params.order))
      }
      if (params.apiaryId) {
        searchParams.append('apiaryId', String(params.apiaryId))
      }
      if (params.orgId) {
        searchParams.append('orgId', String(params.orgId))
      }
      if (params.includeDisabled === true) {
        searchParams.append('includeDisabled', String(params.includeDisabled))
      }
      if (params.templateId) {
        // Ensure templateId is a clean string, remove quotes if present
        searchParams.append(
          'templateId',
          String(params.templateId).replace(/^"(.*)"$/, '$1'),
        )
      }
      if (params.approvalState) {
        searchParams.append('approvalState', String(params.approvalState))
      }
      if (params.hiveState) {
        searchParams.append('hiveState', String(params.hiveState))
      }
      if (params.tags) {
        searchParams.append('tags', String(params.tags))
      }
      if (params.hiveId) {
        searchParams.append('hiveId', String(params.hiveId))
      }
      if (params.compact) {
        searchParams.append('compact', String(params.compact))
      }

      // Build the new URL with the search params directly
      const urlString = `${window.location.pathname}?${searchParams.toString()}`

      // Use navigate to the exact URL string without allowing TanStack to serialize
      navigate({
        to: urlString,
        replace: true,
        // Don't pass search parameters, as they're already in the URL
      })
    },
    [navigate],
  )

  // Correcting the type assignment issue in the helper function
  const updateUrlWithFilterState = useCallback(
    (
      key: keyof ClusterConfigSearchParams,
      value: string | boolean | undefined,
      enhancedQueryParams: Partial<ClusterConfigSearchParams>,
      updateUrlWithoutHistoryEntry: (
        params: Partial<ClusterConfigSearchParams>,
      ) => void,
      setPage: (page: number) => void,
    ) => {
      const newParams = { ...enhancedQueryParams }
      if (value !== undefined) {
        // Special handling for templateId to ensure it's a clean string
        if (key === 'templateId') {
          // @ts-ignore
          newParams[key] = String(value).replace(/^"(.*)"$/, '$1')
        } else {
          // @ts-ignore
          newParams[key] = value
        }
      } else {
        delete newParams[key]
      }
      newParams.page = 1
      setPage(1)
      updateUrlWithoutHistoryEntry(newParams)
    },
    [],
  )

  const handleDeploymentLabelChange = useCallback(
    (option: DeploymentLabelOption | null) => {
      setSelectedDeploymentLabel(option)
      // Reset page when filter changes
      setPage(1)

      // Update URL with new filter state
      updateUrlWithFilterState(
        'apiaryId',
        option?.value,
        enhancedQueryParams,
        updateUrlWithoutHistoryEntry,
        setPage,
      )
    },
    [
      enhancedQueryParams,
      updateUrlWithoutHistoryEntry,
      updateUrlWithFilterState,
    ],
  )

  const handleSortChange = useCallback(
    (sort: FilterOption | null) => {
      setSelectedSortOption(sort as FilterOption)
      setPage(1)

      // Update URL with new filter state
      updateUrlWithFilterState(
        'order',
        sort?.value,
        enhancedQueryParams,
        updateUrlWithoutHistoryEntry,
        setPage,
      )
    },
    [
      enhancedQueryParams,
      updateUrlWithoutHistoryEntry,
      updateUrlWithFilterState,
    ],
  )

  const handleTemplateChange = useCallback(
    (sort: FilterOption | null) => {
      setSelectedTemplate(sort as FilterOption)
      setPage(1)

      // Update URL with new filter state
      updateUrlWithFilterState(
        'templateId',
        sort?.value ? String(sort.value).replace(/^"(.*)"$/, '$1') : undefined,
        enhancedQueryParams,
        updateUrlWithoutHistoryEntry,
        setPage,
      )
    },
    [
      enhancedQueryParams,
      updateUrlWithoutHistoryEntry,
      updateUrlWithFilterState,
    ],
  )

  function toggleIncludeDisabledChange() {
    const newIncludeDisabled = !includeDisabled
    setIncludeDisabled(newIncludeDisabled)
    setPage(1) // Reset page when filter changes

    // Update URL with new filter state using updateUrlWithFilterState
    updateUrlWithFilterState(
      'includeDisabled',
      newIncludeDisabled,
      enhancedQueryParams,
      updateUrlWithoutHistoryEntry,
      setPage,
    )
  }

  // Update the setSelectedTags function with URL updates
  const handleTagsChange = (newTags: FilterOption[]) => {
    setSelectedTags(newTags)
    setPage(1)

    // Update URL with new filter state using updateUrlWithFilterState
    updateUrlWithFilterState(
      'tags',
      newTags && newTags.length > 0
        ? newTags.map((tag) => tag.value).join(',')
        : undefined,
      enhancedQueryParams,
      updateUrlWithoutHistoryEntry,
      setPage,
    )
  }

  // Clear all filters at once rather than one by one
  function clearAllFilters() {
    // Reset all filter states in one go to avoid multiple re-renders
    setSelectedDeploymentLabel(null)
    setSelectedTemplate(null)
    setSelectedApprovalState(null)
    setSelectedHiveState(null)
    setSelectedTags([])
    setSelectedOrgId(null)
    setSelectedHiveId(null)

    // Reset to page 1 after clearing filters
    setPage(1)

    // Use the same URL building approach as updateUrlWithoutHistoryEntry
    const searchParams = new URLSearchParams()
    searchParams.append('page', '1')
    const urlString = `${window.location.pathname}?${searchParams.toString()}`

    // Navigate directly to the URL string
    navigate({
      to: urlString,
      replace: true,
    })
  }

  const skeletonWidths = [32, 32, 250, 100, 100, 60]

  // Handler functions for approvalState and hiveState filters
  const handleApprovalStateChange = useCallback(
    (option: FilterOption | null) => {
      setSelectedApprovalState(option as FilterOption)
      setPage(1)

      // Update URL with new filter state
      updateUrlWithFilterState(
        'approvalState',
        option?.value,
        enhancedQueryParams,
        updateUrlWithoutHistoryEntry,
        setPage,
      )
    },
    [
      enhancedQueryParams,
      updateUrlWithoutHistoryEntry,
      updateUrlWithFilterState,
    ],
  )

  const handleHiveStateChange = useCallback(
    (option: FilterOption | null) => {
      setSelectedHiveState(option as FilterOption)
      setPage(1)

      // Update URL with new filter state
      updateUrlWithFilterState(
        'hiveState',
        option?.value,
        enhancedQueryParams,
        updateUrlWithoutHistoryEntry,
        setPage,
      )
    },
    [
      enhancedQueryParams,
      updateUrlWithoutHistoryEntry,
      updateUrlWithFilterState,
    ],
  )

  // Handle hiveId search (called on Enter key press)
  const handleHiveIdSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim()
      const hiveId = trimmedValue || undefined
      setSelectedHiveId(hiveId || null)

      // Update URL with new filter state
      updateUrlWithFilterState(
        'hiveId',
        hiveId,
        enhancedQueryParams,
        updateUrlWithoutHistoryEntry,
        setPage,
      )
    },
    [
      enhancedQueryParams,
      updateUrlWithoutHistoryEntry,
      updateUrlWithFilterState,
    ],
  )

  // Handle orgId search (called on Enter key press)
  const handleOrgIdSearch = useCallback(
    (value: string) => {
      const orgId = value || undefined
      setSelectedOrgId(orgId || null)

      // Update URL with new filter state
      updateUrlWithFilterState(
        'orgId',
        orgId,
        enhancedQueryParams,
        updateUrlWithoutHistoryEntry,
        setPage,
      )
    },
    [
      enhancedQueryParams,
      updateUrlWithoutHistoryEntry,
      updateUrlWithFilterState,
    ],
  )

  // Track the last clicked checkbox index
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null)

  // Track shift key state
  const [isShiftPressed, setIsShiftPressed] = useState(false)

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Shift') {
        setIsShiftPressed(true)
      }
    }

    function handleKeyUp(event: KeyboardEvent) {
      if (event.key === 'Shift') {
        setIsShiftPressed(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  function handleCheckboxChange(id: string, index: number) {
    if (isShiftPressed && lastClickedIndex !== null) {
      const allRowIds = clusters.data?.revisions
        ? clusters.data.revisions.map((revision) => revision.revision.hive_id)
        : []

      const start = Math.min(lastClickedIndex, index)
      const end = Math.max(lastClickedIndex, index)

      const idsToSelect = allRowIds.slice(start, end + 1)

      setSelectedHives((prevSelected) => {
        const newSelection = new Set(prevSelected)
        for (const id of idsToSelect) {
          newSelection.add(id)
        }
        return Array.from(newSelection)
      })
      return
    }

    if (selectedHives.includes(id)) {
      setSelectedHives(selectedHives.filter((rowId) => rowId !== id))
    } else {
      setSelectedHives([...selectedHives, id])
    }

    setLastClickedIndex(index)
  }

  const hasSelectedHives = selectedHives.length > 0

  function handleCheckboxAllRowsChange() {
    if (
      selectedHives.length === clusters.data?.revisions.length &&
      hasSelectedHives
    ) {
      // Uncheck all rows
      setSelectedHives([])
    } else {
      // Check all rows
      const allRowIds =
        clusters.data?.revisions.map((revision) => revision.revision.hive_id) ||
        []
      setSelectedHives(allRowIds)
    }
  }

  function dismissBulkUpdateModal() {
    setBulkUpdateTemplateId(null)
    onClose()
  }

  function handleBulkUpdateConfirm() {
    if (!bulkUpdateTemplateId) {
      return
    }

    submitBulkClusterUpdate(selectedHives, bulkUpdateTemplateId.value).then(
      () => {
        setSelectedHives([]) // Clear selection after successful update
        dismissBulkUpdateModal()
      },
    )
  }

  const [isCollapsed, setIsCollapsed] = useState(true)

  return (
    <PageLayout
      title={t('sidebar.v3ClusterRevisions')}
      action={
        <HStack>
          <Link to="/clusters/new-hive-wizard">
            <Button
              colorPalette="brand"
              variant={hasSelectedHives ? 'outline' : 'solid'}
            >
              <Icon as={Plus} />
              {t('clusterConfigTable.actions.newConfigWizard')}
            </Button>
          </Link>
          <Link to="/clusters/new-hive">
            <Button
              colorPalette="gray"
              variant={hasSelectedHives ? 'outline' : 'solid'}
            >
              <Icon as={Plus} />
              {t('clusterConfigTable.actions.newConfig')}
            </Button>
          </Link>
        </HStack>
      }
    >
      <ClusterConfigFilters
        selectedOrgId={selectedOrgId}
        handleOrgIdSearch={handleOrgIdSearch}
        selectedDeploymentLabel={selectedDeploymentLabel}
        handleDeploymentLabelChange={handleDeploymentLabelChange}
        templatesData={templatesState.data || []}
        selectedTemplate={selectedTemplate}
        handleTemplateChange={handleTemplateChange}
        approvalStateOptions={approvalStateOptions}
        selectedApprovalState={selectedApprovalState}
        handleApprovalStateChange={handleApprovalStateChange}
        hiveStateOptions={hiveStateOptions}
        selectedHiveState={selectedHiveState}
        handleHiveStateChange={handleHiveStateChange}
        selectedHiveId={selectedHiveId}
        handleHiveIdSearch={handleHiveIdSearch}
        tagOptions={tagOptions}
        selectedTags={selectedTags}
        setSelectedTags={handleTagsChange}
        sortOrderOptions={sortOrderOptions}
        selectedSortOption={selectedSortOption}
        handleSortChange={handleSortChange}
        includeDisabled={includeDisabled}
        toggleIncludeDisabledChange={toggleIncludeDisabledChange}
        hasActiveFilters={hasActiveFilters}
        clearAllFilters={clearAllFilters}
        setPage={(newPage) => {
          const resolvedPage =
            typeof newPage === 'function' ? newPage(page) : newPage
          setPage(resolvedPage)

          // Update URL with new page number using URLSearchParams for consistent handling
          const searchParams = new URLSearchParams()
          searchParams.append('page', String(resolvedPage))

          // Add all other existing parameters
          for (const [key, value] of Object.entries(enhancedQueryParams)) {
            if (key !== 'page' && value !== undefined) {
              searchParams.append(key, String(value))
            }
          }

          // Navigate to the new URL
          const urlString = `${window.location.pathname}?${searchParams.toString()}`
          navigate({
            to: urlString,
            replace: true,
          })
        }}
        copyFilterUrl={copyFilterUrl}
      />

      <Card.Root w="100%">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader
                width="32px"
                height="60px"
                paddingLeft="1rem"
                paddingRight={0}
              >
                <Checkbox.Root
                  checked={
                    selectedHives.length > 0 &&
                    selectedHives.length <
                      (clusters.data?.revisions.length || 0)
                      ? 'indeterminate'
                      : selectedHives.length ===
                          clusters.data?.revisions.length && hasSelectedHives
                  }
                  onCheckedChange={() => {
                    if (
                      selectedHives.length > 0 &&
                      selectedHives.length <
                        (clusters.data?.revisions.length || 0)
                    ) {
                      setSelectedHives([]) // Deselect all hives when in indeterminate state
                    } else {
                      handleCheckboxAllRowsChange()
                    }
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control>
                    <Checkbox.Indicator />
                  </Checkbox.Control>
                </Checkbox.Root>
              </Table.ColumnHeader>
              {hasSelectedHives ? (
                <Table.ColumnHeader colSpan={2} px={4} fontFamily="body">
                  <HStack justifyContent="space-between">
                    <Text
                      fontSize="sm"
                      color="text-secondary"
                      fontWeight={400}
                      letterSpacing={0}
                      textTransform="none"
                    >
                      {t('clusterConfigTable.selectedHives', {
                        count: selectedHives.length,
                      })}
                    </Text>
                    <HStack>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedHives([])}
                      >
                        {t('clusterConfigTable.actions.clearSelection')}
                      </Button>
                      <Button
                        colorPalette="brand"
                        variant="solid"
                        size="sm"
                        onClick={onOpen}
                        disabled={!selectedHives.length}
                      >
                        {t('clusterConfigTable.actions.bulkUpdate')}
                      </Button>
                    </HStack>
                  </HStack>
                </Table.ColumnHeader>
              ) : (
                <>
                  <Table.ColumnHeader width="32px" paddingRight="0" />
                  <Table.ColumnHeader pl={4}>
                    {t('clusterConfigTable.columns.hiveId')}
                  </Table.ColumnHeader>
                </>
              )}
              <Table.ColumnHeader>
                {t('clusterConfigTable.columns.orgId')}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t('clusterConfigTable.columns.deploymentLabel')}
              </Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right" maxWidth="110px">
                {t('clusterConfigTable.columns.templateId')}
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {clusters.isLoading && (
              <SkeletonTable skeletonWidths={skeletonWidths} />
            )}

            {(clusters.isError || !clusters.data) && (
              <Table.Row>
                <Table.Cell colSpan={skeletonWidths.length}>
                  <ApiErrorState
                    apiResource={clusters as unknown as UseQueryResult}
                    errorTitle={t('clusterConfigTable.errors.title')}
                    errorDescription={t(
                      'clusterConfigTable.errors.description',
                    )}
                  />
                </Table.Cell>
              </Table.Row>
            )}

            {clusters.isSuccess && clusters?.data?.revisions?.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={skeletonWidths.length}>
                  <Text>{t('clusterConfigTable.noResults')}</Text>
                </Table.Cell>
              </Table.Row>
            )}

            {clusters.isSuccess &&
              clusters?.data?.revisions?.length > 0 &&
              clusters?.data?.revisions?.map((latestRevision, index) => {
                const { revision } = latestRevision
                const hiveIsSelected = selectedHives.includes(revision.hive_id)
                return (
                  <Table.Row
                    key={revision.hive_id}
                    className={styles.trAlignContentTop}
                    _hover={{
                      backgroundColor: hiveIsSelected
                        ? 'blue.100'
                        : 'var(--chakra-colors-shell-muted)',
                    }}
                    backgroundColor={hiveIsSelected ? 'blue.50' : 'transparent'}
                  >
                    <Table.Cell
                      data-testid="checkbox"
                      className={styles.trAlignContentTopTdCheckbox}
                    >
                      <Checkbox.Root
                        checked={selectedHives.includes(revision.hive_id)}
                        onCheckedChange={() =>
                          handleCheckboxChange(revision.hive_id, index)
                        }
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                      </Checkbox.Root>
                    </Table.Cell>
                    <Table.Cell
                      data-testid="combined-state"
                      className={styles.trAlignContentTopTdIcon}
                      width="32px"
                    >
                      <ClusterConfigCombinedStates revision={latestRevision} />
                    </Table.Cell>
                    <Table.Cell data-testid="hive-name">
                      <Flex direction="column" gap="1">
                        <HStack justifyContent="space-between">
                          <DataTag
                            fontWeight={400}
                            value={revision.hive_id}
                            hasCopy={true}
                            maxWidthPx={250}
                            onClickBody={() =>
                              navigate({
                                to: clusterConfigsRootRoute.to,
                                params: {
                                  orgId: revision.org_id,
                                  clusterId: revision.hive_id,
                                },
                              })
                            }
                          >
                            {revision.hive_id}
                          </DataTag>
                          {latestRevision.ignore_for_reconcile && (
                            <ClusterSyncDisabled />
                          )}
                        </HStack>
                        {!compact && (
                          <Text fontSize="sm" color="gray">
                            {capitalize(revision.approval_state.state)}
                            {NBSP}
                            {revisionActionMetadata(latestRevision)}
                          </Text>
                        )}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell data-testid="org-id" width={100}>
                      <DataTag
                        fontWeight={400}
                        value={revision.org_id}
                        hasCopy={true}
                        onClickBody={() =>
                          navigate({
                            to: hivesOverviewOrganizationDetailsRoute.to,
                            params: {
                              orgId: revision.org_id,
                            },
                          })
                        }
                      >
                        {revision.org_id}
                      </DataTag>
                    </Table.Cell>
                    <Table.Cell data-testid="deployment-label" width={265}>
                      <DataTag
                        fontWeight={400}
                        value={revision.deployment_label}
                        hasCopy={true}
                        maxWidthPx={200}
                      >
                        {revision.deployment_label}
                      </DataTag>
                    </Table.Cell>
                    <Table.Cell
                      data-testid="template-id"
                      maxWidth="110px"
                      className={styles.trAlignContentTopTdTemplateId}
                    >
                      <Text>{revision.template_id}</Text>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
          </Table.Body>
        </Table.Root>
      </Card.Root>

      {clusters.isSuccess && (
        <TableFooter
          page={page}
          totalPages={clusters.data?.total_pages ?? page}
          hasNextPage={Boolean(clusters.data?.has_next_page)}
          onFirstPage={goToFirstPage}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onLastPage={goToLastPage}
        />
      )}

      <Dialog.Root
        open={isOpen}
        onOpenChange={(details) => !details.open && dismissBulkUpdateModal()}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{t('bulkUpdate.modal.title')}</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="start" gap={6}>
                <VStack align="start">
                  <Text>
                    <Trans
                      i18nKey="bulkUpdate.modal.updateCount"
                      count={selectedHives.length}
                    />
                  </Text>

                  {selectedHives.length > 0 && (
                    <VStack>
                      <Button
                        variant="ghost"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        alignSelf="flex-start"
                        size="sm"
                      >
                        <Icon as={isCollapsed ? ChevronRight : ChevronDown} />
                        {isCollapsed
                          ? t('bulkUpdate.modal.showClusterIds')
                          : t('bulkUpdate.modal.hideClusterIds')}
                      </Button>

                      <Collapsible.Root open={!isCollapsed}>
                        <Collapsible.Content>
                          <List.Root maxHeight="200px" overflow="auto" px={6}>
                            {selectedHives.map((clusterId) => (
                              <List.Item key={`bulk-update-modal-${clusterId}`}>
                                <Text fontSize="sm">{clusterId}</Text>
                              </List.Item>
                            ))}
                          </List.Root>
                        </Collapsible.Content>
                      </Collapsible.Root>
                    </VStack>
                  )}
                </VStack>

                <VStack align="start">
                  <Text>{t('bulkUpdate.modal.updateDescription')}</Text>
                  <List.Root>
                    {(
                      t('bulkUpdate.modal.updateDescriptionSteps', {
                        clusters: selectedHives,
                        returnObjects: true,
                      }) as string[]
                    ).map((msg) => (
                      <List.Item key={msg}>
                        <Text fontSize="md">{msg}</Text>
                      </List.Item>
                    ))}
                  </List.Root>
                </VStack>

                <Field.Root required>
                  <Field.Label w={36} htmlFor="template-id">
                    {t('bulkUpdate.modal.templateLabel')}
                  </Field.Label>
                  <Select.Root
                    collection={templatesCollection}
                    value={
                      bulkUpdateTemplateId ? [bulkUpdateTemplateId.value] : []
                    }
                    onValueChange={(e) => {
                      const value = e.value[0]
                      if (value) {
                        const selectedTemplate = (
                          templatesState.data || []
                        ).find((data) => data.template_id === value)
                        setBulkUpdateTemplateId(
                          selectedTemplate
                            ? {
                                value: selectedTemplate.template_id,
                                label:
                                  selectedTemplate.name ??
                                  selectedTemplate.template_id,
                              }
                            : null,
                        )
                      } else {
                        setBulkUpdateTemplateId(null)
                      }
                    }}
                    positioning={{ sameWidth: true }}
                  >
                    <Select.HiddenSelect />
                    <Select.Control>
                      <Select.Trigger>
                        <SelectValueWithTag placeholder="Please select a Template for the update" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content onScroll={handleTemplatesScroll}>
                        {templatesCollection.items.map((template) => (
                          <Select.Item item={template} key={template.value}>
                            <HStack gap={2}>
                              <Tag.Root size="sm">
                                <Tag.Label>{template.value}</Tag.Label>
                              </Tag.Root>
                              <Text>{template.label}</Text>
                            </HStack>
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={dismissBulkUpdateModal} mr={3}>
                {t('bulkUpdate.modal.cancelButton')}
              </Button>
              <Button
                colorPalette={bulkUpdateTemplateId ? 'red' : 'gray'}
                onClick={handleBulkUpdateConfirm}
                disabled={!bulkUpdateTemplateId}
                cursor={bulkUpdateTemplateId ? 'pointer' : 'not-allowed'}
                _hover={
                  bulkUpdateTemplateId
                    ? { cursor: 'pointer' }
                    : { cursor: 'not-allowed' }
                }
              >
                {t('bulkUpdate.modal.confirmButton')}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </PageLayout>
  )
}
